import React, { useState, useEffect, useCallback } from 'react';
import { useAPI } from '../../../clients/quipli-service/quipli-service-client';
import { Typography } from '../../../components/typography/typography';
import { debounce, partition } from 'lodash';
import { DateTime } from 'luxon';
import { SelectForm } from '../../../components/select-form/select-form';
import { useSearchParams } from 'react-router-dom';
import useRequestQueryParams from '../../../hooks/use-request-query-params';
import { toast } from '../../../util/toasts';
import { Admin } from '../../../clients/quipli-service/admin/admin';
import { useAdminApiV1 } from '../../../clients/quipli-service/admin/admin-api-v1';
import { Button } from '../../../components/form/button';
import Icon from '../../../components/icons/icon';
import FileSaver from 'file-saver';
import { displayMoneyFormat, safeRound } from '../../../util/util';
import { Spinner } from '../../../components/spinner/spinner';
import { useReportApiV2 } from '../../../clients/quipli-service/report/report-api-v2';
import { AdminCommission, DateTimeRange, SearchCommissionRequest } from '../../../clients/quipli-service/report/report';
import classNames from 'classnames';
import { Card } from '../../../components/card/card';
import '../../collectibles/collectibles.scss';
import { CommissionInvoices } from './commission-invoices';
import { SpinnerModal } from '../../../components/modal/spinner-modal';

export const SalesCommission = () => {
  const { getMultipleConfigurations } = useAPI();
  const { getAdminsList } = useAdminApiV1();
  const { getSalesCommissionReport, exportSalesCommissionReport } = useReportApiV2();

  const [timezone, setTimezone] = useState<string>();
  const [currency, setCurrency] = useState<string>();
  const [data, setData] = useState<AdminCommission[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [admins, setAdmins] = useState<Admin[]>([]);
  const [expandedAdmin, setExpandedAdmin] = useState<number>();
  const [showInvoices, setShowInvoices] = useState<{ admin: Admin; invoiceIds: string[] }>();
  const [isExporting, setIsExporting] = useState<boolean>(false);

  const dateRangeDropdown = { from: DateTime.now().minus({ years: 2 }), to: DateTime.now().plus({ months: 6 }) };

  // List request hooks
  const [params] = useSearchParams();
  const { request, setRequest } = useRequestQueryParams<SearchCommissionRequest>(
    {
      startDate:
        params.has('startDate') && DateTime.fromISO(params.get('startDate')!).isValid
          ? DateTime.fromISO(params.get('startDate')!)
          : DateTime.now().set({ day: 1, hour: 0, minute: 0, second: 0, millisecond: 0 }),
      endDate:
        params.has('endDate') && DateTime.fromISO(params.get('endDate')!).isValid
          ? DateTime.fromISO(params.get('endDate')!)
          : DateTime.now().set({ day: 1, hour: 0, minute: 0, second: 0, millisecond: 0 }),
      adminId:
        params.has('adminId') && Number.isNaN(parseInt(params.get('adminId')!))
          ? parseInt(params.get('adminId')!)
          : undefined,
    },
    () => {},
  );

  useEffect(() => {
    loadTimezone();
  }, []);

  const loadTimezone = async () => {
    const [confs, _admins] = await Promise.all([
      getMultipleConfigurations(['CONF_TIMEZONE', 'CONF_CURRENCY']),
      getAdminsList(),
    ]);
    const tz = confs.find((c) => c.variable === 'CONF_TIMEZONE')?.value;
    const currency = confs.find((c) => c.variable === 'CONF_CURRENCY')?.value;
    setCurrency(currency || 'USD');
    setTimezone(tz);
    setAdmins(partition(_admins, (a) => !a.deletedAt).flat());
  };

  const fetchCommission = useCallback(
    debounce(async (req: SearchCommissionRequest) => {
      setIsLoading(true);
      try {
        const items = await getSalesCommissionReport({
          startDate: req.startDate,
          endDate: req.endDate,
          adminId: req.adminId,
        });
        setData(items);
      } catch (err) {
        toast('Error loading commission data', 'error');
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    }, 600),
    [],
  );

  useEffect(() => {
    fetchCommission(request);
  }, [request]);

  const doExport = async () => {
    setIsExporting(true);
    try {
      const file = await exportSalesCommissionReport({
        startDate: request.startDate,
        endDate: request.endDate,
        adminId: request.adminId,
      });

      const exportFileName = `Sales_Commission_${
        (request.adminId && admins.find((a) => a.adminId === request.adminId)?.adminFullName) ?? ''
      }_${request.startDate.toFormat('yyyy/MM/dd')}-${request.endDate.toFormat('yyyy/MM/dd')}`;
      const fileType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
      const fileExtension = '.xlsx';

      const data = new Blob([file], { type: fileType });
      FileSaver.saveAs(data, `${exportFileName}${fileExtension}`);
    } catch (e) {
      console.log(e);
      toast('Failed to export commission report', 'error');
    } finally {
      setIsExporting(false);
    }
  };

  const reportingMonths: DateTimeRange[] = [];
  if (dateRangeDropdown) {
    let currentStart = dateRangeDropdown.from.startOf('month');
    while (currentStart.diffNow().milliseconds < 0) {
      reportingMonths.push({ from: currentStart, to: currentStart.endOf('month') });
      currentStart = currentStart.plus({ months: 1 }).setZone(timezone, { keepLocalTime: true });
    }
  }
  reportingMonths.reverse();
  let selectedMonth = -1;
  if (request.startDate && request.endDate) {
    selectedMonth = reportingMonths.findIndex(
      (m) => m.from.diff(request.startDate!).milliseconds === 0 && m.to.diff(request.endDate!).milliseconds === 0,
    );
  }

  return (
    <div className="collectibles">
      <Card>
        {!showInvoices && (
          <>
            <div className="flex justify-between items-center">
              <Typography type="display-xs" weight="medium" className="text-[var(--grey-800)]">
                Sales Commission
              </Typography>
            </div>

            <div className="pt-2 pb-4 flex justify-between gap-2">
              <div className="flex gap-2 flex-wrap">
                <select
                  className="h-fit text-[14px]"
                  value={selectedMonth}
                  required={false}
                  onChange={(e) => {
                    const range = reportingMonths[parseInt(e.target.value)];
                    setRequest({ ...request, startDate: range.from, endDate: range.to });
                  }}
                >
                  {selectedMonth < 0 && <option value={-1}></option>}
                  {reportingMonths.map((m, i) => (
                    <option key={i} value={i}>
                      {m.from.toFormat('LLLL yyyy')}
                    </option>
                  ))}
                </select>
                <div className="col-span-1 sm:col-span-1">
                  <SelectForm
                    className="filter-dropdown"
                    options={admins}
                    onChange={(admin) => {
                      setRequest({ ...request, adminId: admin ? admin.adminId : undefined });
                    }}
                    isClearable={true}
                    placeholder="Sales Rep"
                    value={admins.find((a) => a.adminId === request.adminId)}
                    getOptionLabel={(a) => `${a.adminFullName}${a.deletedAt ? ' (Deactivated)' : ''}`}
                  />
                </div>
              </div>

              <Button className="button-yellow h-[40px]" onClick={() => doExport()} data-cy="export">
                Export
                <Icon icon="Export" size="medium" stroke="var(--grey-700)" />
              </Button>
            </div>
          </>
        )}

        {isLoading ? (
          <Spinner />
        ) : !showInvoices ? (
          <div className="invoice-table">
            <table className=" w-full">
              <thead>
                <tr>
                  <th className="w-[40%] text-left">Sales Rep</th>
                  <th className="w-[20%] text-right">Total</th>
                  <th className="w-[20%] text-right">Commission Rate</th>
                  <th className="w-[20%] text-right">Payout</th>
                  <th className="text-left"> </th>
                </tr>
              </thead>
              <tbody>
                {data.length ? (
                  data.map((row) => {
                    const multipleCommissionRates = row.generalLedgerTotals.some(
                      (t) => t.rate !== row.generalLedgerTotals[0].rate,
                    );
                    return (
                      <React.Fragment>
                        <tr
                          className={classNames('invoice-row', { expanded: expandedAdmin === row.admin.adminId })}
                          onClick={() =>
                            expandedAdmin === row.admin.adminId
                              ? setExpandedAdmin(undefined)
                              : setExpandedAdmin(row.admin.adminId)
                          }
                        >
                          <td className="text-left">
                            {row.admin.adminFullName}
                            {row.admin.deletedAt ? ' (Deactivated)' : ''}
                          </td>
                          <td className="text-right">{displayMoneyFormat(row.subtotal, currency)}</td>
                          <td className="text-right">
                            {multipleCommissionRates
                              ? '*Multiple rates'
                              : `${safeRound(row.generalLedgerTotals[0].rate * 100, 6)}%`}
                          </td>
                          <td className="text-right">{displayMoneyFormat(row.commissionTotal, currency)}</td>
                          <td className="text-left">
                            <Icon icon={expandedAdmin === row.admin.adminId ? 'ChevronUp' : 'ChevronDown'} />
                          </td>
                        </tr>
                        {expandedAdmin === row.admin.adminId && (
                          <tr
                            className={classNames('invoice-detail', { expanded: expandedAdmin === row.admin.adminId })}
                          >
                            <td colSpan={100}>
                              <div className="p-2 flex flex-column gap-2">
                                <Card className="w-full">
                                  <div className="flex justify-between">
                                    <h6 className="p-2">Commission Breakdown</h6>
                                    <div
                                      className="flex flex-end items-center gap-2 underline"
                                      onClick={() => {
                                        setShowInvoices({ admin: row.admin, invoiceIds: row.invoiceIds });
                                      }}
                                    >
                                      View Invoices <Icon size="small" icon="ForwardArrow" />
                                    </div>
                                  </div>
                                  <table className="w-full">
                                    <tbody>
                                      {row.generalLedgerTotals.map((glt) => (
                                        <tr>
                                          <td className="w-[40%] text-left">{glt.generalLedgerAccount.name}</td>
                                          <td className="w-[20%] text-right">
                                            {displayMoneyFormat(glt.generalLedgerAccountTotal, currency)}
                                          </td>
                                          <td className="w-[20%] text-right">{`${safeRound(glt.rate * 100, 6)}%`}</td>
                                          <td className="w-[20%] text-right">
                                            {displayMoneyFormat(glt.commissionTotal, currency)}
                                          </td>
                                          <td className="text-left">
                                            <Icon
                                              className="invisible"
                                              icon={expandedAdmin === row.admin.adminId ? 'ChevronUp' : 'ChevronDown'}
                                            />
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </Card>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })
                ) : (
                  <></>
                )}
              </tbody>
            </table>

            {!data.length && (
              <Typography type="text-md" className="text-center mt-2">
                No commission for specified period
              </Typography>
            )}
          </div>
        ) : (
          <CommissionInvoices
            admin={showInvoices.admin}
            commissionPeriod={request.startDate}
            invoiceIds={showInvoices.invoiceIds}
            backToCommission={() => setShowInvoices(undefined)}
          />
        )}
      </Card>
      <SpinnerModal isOpen={isExporting} contentDescription="Exporting Sales Commission Report" />
    </div>
  );
};
